import { useCallback, useState } from 'react'

import Head from 'next/head'

import { useLongPress } from 'use-long-press'

import { BackButton } from '~/components/action/BackButton'
import { CloseIcon, EditIcon, MessagePlusIcon } from '~/components/icons'
import { Container, ContainerInset } from '~/components/layout/Container'
import { Header } from '~/components/layout/Header'

export const DictionaryDialogContentList: React.FC<{
  dictionary:
    | {
        id: string
        text: string
        isOfficial: boolean
      }[]
    | undefined
  onBack: () => void
  onSelect: (text: string) => void
  onNew: () => void
  onEdit: (term: { id: string; text: string }) => void
  onDelete: (id: string) => void
}> = (props) => {
  return (
    <>
      <Head>
        <title>辞書登録</title>
      </Head>

      <Header>
        <div className="w-5">
          <BackButton onClick={props.onBack} />
        </div>
        辞書登録
        <div className="w-5">
          <button onClick={() => props.onNew()}>
            <MessagePlusIcon className="fill-white" />
          </button>
        </div>
      </Header>

      <Container>
        <ContainerInset>
          <div className="text-sm">
            <div className="mb-5">
              登録されているテキストをタップすると自由記載欄に追加されます。辞書の新規登録は右上の＋ボタンから作成をお願いします。ユーザー登録したフレーズの編集・削除はテキスト長押しで操作可能です。
            </div>

            {props.dictionary?.map((term) => {
              return (
                <div key={term.id}>
                  <DictionaryTermRow
                    term={term}
                    onSelect={props.onSelect}
                    onEdit={props.onEdit}
                    onDelete={props.onDelete}
                  />
                </div>
              )
            })}
          </div>
        </ContainerInset>
      </Container>
    </>
  )
}

const DictionaryTermRow: React.FC<{
  term: {
    id: string
    isOfficial: boolean
    text: string
  }
  onSelect: (text: string) => void
  onEdit: (term: { id: string; text: string }) => void
  onDelete: (id: string) => void
}> = (props) => {
  const [expand, setExpand] = useState<boolean>()

  const longPressCallback = useCallback(() => {
    if (!props.term.isOfficial) {
      setExpand(true)
    }
  }, [props.term.isOfficial])

  const handleLongPress = useLongPress(longPressCallback, {
    onStart: () => {
      document.body.style.userSelect = 'none'
    },
    onCancel: (_, meta) => {
      document.body.style.userSelect = 'initial'
      if (meta.reason === 'cancelled-by-release') {
        if (expand) {
          setExpand(false)
        } else {
          props.onSelect(props.term.text)
        }
      }
    },
    onFinish: () => {
      document.body.style.userSelect = 'initial'
    },
  })

  return (
    <div className="relative">
      <button
        type="button"
        {...handleLongPress()}
        className="py-5 border-t border-gray w-full text-left"
      >
        {props.term.isOfficial ? (
          <div className="w-16 py-1 rounded-full bg-function-cv1 text-white text-xs text-center">
            公式
          </div>
        ) : (
          <div className="w-16 py-1 rounded-full bg-secondary text-white text-xs text-center">
            ユーザー
          </div>
        )}
        <div className="mt-2 whitespace-pre-wrap break-words">
          {props.term.text}
        </div>
      </button>
      {/* NOTE: 外に出すことで handleLongPress と干渉しないようにしている */}
      {!props.term.isOfficial && expand && (
        <div className="absolute right-0 top-5 flex gap-3">
          <button type="button" onClick={() => props.onEdit(props.term)}>
            <EditIcon className="fill-gray" />
          </button>
          <button type="button" onClick={() => props.onDelete(props.term.id)}>
            <CloseIcon className="fill-gray" />
          </button>
        </div>
      )}
    </div>
  )
}
